import { useEffect } from "react";
import { useNavigate, NavLink } from "react-router-dom";
import { motion } from "framer-motion";
import { FileText, CheckCircle2, Clock, ShieldCheck, ArrowRight, Briefcase } from "lucide-react";
import FXBackdrop from "../components/FXBackdrop";
import { useAuth } from "../contexts/AuthContext";
import PageTransition from "../components/PageTransition";

const fade = { initial: { opacity: 0, y: 18 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: false, amount: 0.1 }, transition: { duration: 0.6 } };

export default function EngineerDashboard() {
    const { user, isAuthenticated, loading } = useAuth();
    const navigate = useNavigate();

    // Redirect if not engineer
    useEffect(() => {
        if (loading) return;
        if (!isAuthenticated) navigate("/login");
        else if (user?.role !== "engineer") navigate("/");
    }, [isAuthenticated, user, loading, navigate]);

    if (loading || !user) return null;

    const profile = user.profile || {};
    
    // Check what is missing in profile
    const hasSocials = profile.linkedin_url || profile.github_url;
    const hasExperience = profile.years_experience || profile.tech_stack;
    const hasPreferences = profile.desired_role || profile.salary_expectation;
    const isProfileComplete = hasSocials && hasExperience && hasPreferences;

    return (
        <PageTransition>
            <FXBackdrop>
                <section className="containerX pt-10 md:pt-16 pb-16">
                    <motion.div {...fade} className="max-w-4xl mx-auto">
                        <div className="chip mb-5">
                            <span className="h-2 w-2 rounded-full" style={{ background: "rgb(16, 185, 129)" }} />
                            Registration Complete
                        </div>
                        
                        <div className="grid lg:grid-cols-3 gap-6 items-start">
                            {/* Left Column: Welcome & Status */}
                            <div className="lg:col-span-2 space-y-6">
                                <div className="glass p-8 md:p-10 relative overflow-hidden">
                                    <div className="absolute top-0 right-0 w-64 h-64 bg-violet-400 opacity-10 blur-3xl rounded-full transform translate-x-1/2 -translate-y-1/2 pointer-events-none" />
                                    
                                    <h1 className="h2 text-3xl">Welcome to the Network, <span className="text-violet-600">{profile.first_name || "Engineer"}</span>!</h1>
                                    <p className="p mt-3 text-lg">
                                        Your profile has been successfully saved. We vet every candidate to ensure high-signal matches for our startup partners.
                                    </p>
                                    
                                    <div className="mt-8 rounded-2xl border p-5" style={{ borderColor: "rgba(226,232,240,0.9)", background: "rgba(255,255,255,0.4)" }}>
                                        <div className="flex items-center gap-3 mb-4">
                                            <div className="h-10 w-10 rounded-xl bg-violet-100 flex items-center justify-center text-violet-600">
                                                <Clock size={20} />
                                            </div>
                                            <div>
                                                <h3 className="font-semibold text-slate-900">What happens next?</h3>
                                                <div className="text-sm text-slate-500">Our process takes typically 48-72 hours.</div>
                                            </div>
                                        </div>
                                        
                                        <div className="space-y-4 relative before:absolute before:inset-0 before:ml-5 before:-translate-x-px md:before:mx-auto md:before:translate-x-0 before:h-full before:w-0.5 before:bg-gradient-to-b before:from-transparent before:via-slate-200 before:to-transparent">
                                            {[
                                                { icon: ShieldCheck, title: "Profile Review", desc: "Our team is reviewing your tech stack and experience.", active: true },
                                                { icon: Briefcase, title: "Matching Pipeline", desc: "You'll be added to our AI-driven matching pool.", active: false },
                                                { icon: CheckCircle2, title: "Role Introductions", desc: "We'll notify you when a startup requests an interview.", active: false },
                                            ].map((step, i) => (
                                                <div key={i} className="relative flex items-center justify-between md:justify-normal md:odd:flex-row-reverse group is-active">
                                                    <div className="flex items-center justify-center w-6 h-6 rounded-full border-2 border-white bg-slate-200 text-slate-500 z-10 
                                                                    group-[.is-active]:bg-violet-600 group-[.is-active]:text-white shadow shrink-0 md:order-1 md:group-odd:-translate-x-1/2 md:group-even:translate-x-1/2"
                                                         style={{ background: step.active ? 'rgb(139,92,246)' : '#e2e8f0', color: step.active ? 'white' : 'transparent' }}>
                                                        {step.active && <CheckCircle2 size={12} />}
                                                    </div>
                                                    
                                                    <div className="w-[calc(100%-2.5rem)] md:w-[calc(50%-1.5rem)] glass p-4 rounded-xl shadow-sm transition hover:shadow-md">
                                                        <div className="flex items-center justify-between space-x-2 mb-1">
                                                            <div className="font-semibold text-slate-900 text-sm">{step.title}</div>
                                                            {step.active && <span className="text-[10px] uppercase font-bold tracking-wider text-violet-600 bg-violet-100 px-2 py-0.5 rounded-full">In Progress</span>}
                                                        </div>
                                                        <div className="text-xs text-slate-500">{step.desc}</div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>
                            
                            {/* Right Column: Profile Status */}
                            <div className="space-y-6">
                                <div className="glass p-6 md:p-8 relative">
                                    <h3 className="font-semibold text-lg border-b pb-3 mb-4" style={{ borderColor: "rgba(226,232,240,0.8)" }}>Profile Completeness</h3>
                                    
                                    <div className="space-y-4">
                                        <div className="flex items-start gap-3">
                                            {isProfileComplete ? <CheckCircle2 size={18} className="text-green-500 mt-0.5 shrink-0" /> : <div className="h-4 w-4 border-2 rounded-full border-slate-300 mt-0.5 shrink-0" />}
                                            <div>
                                                <div className="text-sm font-medium text-slate-900">Basic Information</div>
                                                <div className="text-xs text-slate-500 mt-0.5">Name & account setup</div>
                                            </div>
                                        </div>
                                        
                                        <div className="flex items-start gap-3">
                                            {hasSocials ? <CheckCircle2 size={18} className="text-green-500 mt-0.5 shrink-0" /> : <div className="h-4 w-4 border-2 rounded-full border-slate-300 mt-0.5 shrink-0" />}
                                            <div>
                                                <div className="text-sm font-medium text-slate-900">Social Profiles</div>
                                                <div className="text-xs text-slate-500 mt-0.5 flex gap-2">
                                                    <span className={profile.linkedin_url ? "text-slate-700" : ""}>LinkedIn</span> • 
                                                    <span className={profile.github_url ? "text-slate-700" : ""}>GitHub</span>
                                                </div>
                                            </div>
                                        </div>
                                        
                                        <div className="flex items-start gap-3">
                                            {hasExperience ? <CheckCircle2 size={18} className="text-green-500 mt-0.5 shrink-0" /> : <div className="h-4 w-4 border-2 rounded-full border-slate-300 mt-0.5 shrink-0" />}
                                            <div>
                                                <div className="text-sm font-medium text-slate-900">Experience</div>
                                                <div className="text-xs text-slate-500 mt-0.5">Years & Top skills</div>
                                            </div>
                                        </div>
                                        
                                        <div className="flex items-start gap-3">
                                            {hasPreferences ? <CheckCircle2 size={18} className="text-green-500 mt-0.5 shrink-0" /> : <div className="h-4 w-4 border-2 rounded-full border-slate-300 mt-0.5 shrink-0" />}
                                            <div>
                                                <div className="text-sm font-medium text-slate-900">Preferences</div>
                                                <div className="text-xs text-slate-500 mt-0.5">Role & Target Salary</div>
                                            </div>
                                        </div>
                                    </div>
                                    
                                    {!isProfileComplete && (
                                        <div className="mt-6 pt-4 border-t" style={{ borderColor: "rgba(226,232,240,0.8)" }}>
                                            <div className="text-xs text-amber-600 font-medium bg-amber-50 p-2.5 rounded-lg mb-3">
                                                Complete your profile to increase your match ranking by 40%.
                                            </div>
                                            <NavLink to="/complete-profile" className="btn btnPrimary w-full justify-center py-2 text-sm">
                                                Complete Profile <ArrowRight size={14} />
                                            </NavLink>
                                        </div>
                                    )}
                                </div>
                                
                                <div className="bg-slate-900 rounded-2xl p-6 text-white overflow-hidden relative">
                                    <div className="absolute -right-4 -bottom-4 opacity-10">
                                        <FileText size={100} />
                                    </div>
                                    <div className="text-sm font-medium text-violet-300 mb-1">Preparation</div>
                                    <h3 className="font-semibold text-lg mb-2">Need a CV review?</h3>
                                    <p className="text-sm text-slate-400 mb-4 line-clamp-3">
                                        Our internal talent team can review your resume before it reaches startups to help you stand out.
                                    </p>
                                    <a href="mailto:contact@hirrd.com?subject=CV Review Request" className="text-white text-sm font-medium flex items-center gap-1 hover:text-violet-300 transition">
                                        Request review <ArrowRight size={14} />
                                    </a>
                                </div>
                            </div>
                        </div>
                    </motion.div>
                </section>
            </FXBackdrop>
        </PageTransition>
    );
}
